// src/utils/tickets.js
// Shared, defensive helpers for loading the FieldTicket list so that HomePage
// (mobile/pumper view) and TicketGrid (desktop/admin view) normalize the API
// response identically.
//
// The /api/tickets.php endpoint normally returns a bare JSON array, but a stale
// service worker, a proxy, an auth redirect, or an error page can hand back HTML,
// an error object, or a wrapped object. Treating any of those as "[]" silently
// blanks the list. These helpers distinguish "valid (possibly empty) list" from
// "not a usable response" so callers can keep the last-good data instead.

/**
 * Coerce an already-parsed JSON payload into a ticket array.
 * Accepts: a bare array, or a wrapper object exposing `tickets` / `data` / `rows`.
 * Returns the array on success, or `null` when the shape is unrecognized
 * (HTML/auth-error/etc.) — callers should treat `null` as "load failed".
 */
export const normalizeTicketsResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.tickets)) return payload.tickets;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.rows)) return payload.rows;
  }
  return null;
};

/** Build a short, non-sensitive description of a parsed payload's shape. */
const describeShape = (json) => {
  if (Array.isArray(json)) return `array(${json.length})`;
  if (json && typeof json === "object") {
    return `object{${Object.keys(json).slice(0, 8).join(",")}}`;
  }
  return typeof json;
};

/**
 * Fetch + parse the tickets endpoint defensively.
 * Resolves to { list, error, status, contentType, shape } where:
 *   - list        : ticket array on success (may be empty), or null on any failure
 *   - error       : null on success, otherwise a short, non-sensitive reason string
 *   - status      : HTTP status (0 if the request never completed)
 *   - contentType : response Content-Type header (for diagnostics)
 *   - shape       : short, non-sensitive summary of the parsed body
 * Never throws. The extra fields exist for the gated debug panel; callers that
 * only destructure { list, error } are unaffected.
 */
export const fetchTicketList = async (url, fetchOpts) => {
  const meta = { status: 0, contentType: "", shape: "" };
  let res;
  try {
    res = await fetch(url, fetchOpts);
  } catch (e) {
    return { list: null, error: "network", ...meta };
  }
  meta.status = res.status;
  meta.contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    return { list: null, error: `http_${res.status}`, ...meta };
  }
  let json;
  try {
    json = await res.json();
  } catch (e) {
    // Body wasn't JSON — almost always an HTML error/login page.
    return { list: null, error: "non_json", ...meta };
  }
  meta.shape = describeShape(json);
  const list = normalizeTicketsResponse(json);
  if (list === null) {
    return { list: null, error: "unexpected_shape", ...meta };
  }
  return { list, error: null, ...meta };
};

/**
 * Case/space-insensitive identity match for ticket ownership. UserIDs are unique
 * login handles, so trimming and lowercasing is business-safe and prevents a
 * stray " David" / "david" in the data from filtering the real owner out.
 */
export const sameUser = (a, b) => {
  const sa = String(a == null ? "" : a).trim();
  const sb = String(b == null ? "" : b).trim();
  return sa === sb || sa.toLowerCase() === sb.toLowerCase();
};

/** Robust "is this ticket billed?" — tolerates Y/y/1/true/"true"/"yes". */
export const isBilled = (v) => {
  if (v === true) return true;
  const s = String(v == null ? "" : v).trim().toLowerCase();
  return s === "y" || s === "1" || s === "true" || s === "yes";
};
